/**
 *
 *  ProgressiveImage
 *  Component
 *
 *  Created by Vladimir Shishlyannikov on 27/05/2025.
 *  Copyright (c) 2025-present iWoo Ltd. All rights reserved.
 *
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import PropTypes from "prop-types"; // use directly
import { Image as Img } from "../Image/Image";
import { Skeleton } from "../Skeleton/Skeleton";

// Hooks
import { useIntersectionObserver } from "../../hooks/useIntersectionObserver/useIntersectionObserver";

// Utils
import { cn, defaultPixelRatio as pixelDensity } from "../../utils";

// Images
import TruBeLogo from "../../icons/TruBeLogo";

export const ProgressiveImage = ({
  src,
  alt = "",
  theme,
  imgConfig,
  doNotPreload,
  defaultBreakpoint,
  width,
  height,
  style,
  className,
  imgStyle,
  rootMargin,
  intersectionThreshold = 0.1,
  loaderClassName,
  showLogo = true,
  logoWidth,
  logoHeight,
}) => {
  const [isLoaded, setIsLoaded] = useState(false);

  // Lazy loading by intersection observer!
  const { ref, inView } = useIntersectionObserver(
    {
      triggerOnce: true,
      threshold: intersectionThreshold,
      rootMargin: rootMargin || "100px 0px 200px 0px", //"500px 0px 1000px 0px",
    },
    []
  );

  // Функция для маппинга breakpoint → media запрос (настраивай под твой theme)
  const getMediaByBreakpoint = (breakpoint) => {
    if (!theme?.responsive?.breakpoints) {
      return undefined;
    }
    const bp = theme.responsive.breakpoints[breakpoint];
    // Пример: для "xs" = 0, для "md" = 768
    if (bp) {
      return `(min-width: ${bp}px)`;
    }
    if (breakpoint === "xs") {
      return undefined;
    } // default
    return undefined;
  };

  // Выбор дефолтной картинки
  const defaultConfig =
    imgConfig.find((item) => item.breakpoint === defaultBreakpoint) ||
    imgConfig[0];

  return (
    <div
      ref={ref}
      style={{
        // position: "relative",
        width,
        height,
        ...style,
      }}
      className={cn(
        // "tbk-relative",
        className
      )}
    >
      {(!isLoaded || !inView) && !doNotPreload && (
        <Skeleton
          className={cn(
            "tbk-absolute tbk-left-0 tbk-top-0 tbk-z-1 tbk-h-full tbk-w-full tbk-rounded-2xl tbk-bg-blue-grey-light",
            loaderClassName
          )}
        >
          {showLogo && (
            <TruBeLogo
              className="tbk-absolute tbk-left-1/2 tbk-top-1/2 tbk-max-h-8 tbk-shrink tbk--translate-x-1/2 tbk--translate-y-1/2"
              width={logoWidth}
              height={logoHeight}
            />
          )}
        </Skeleton>
      )}

      {(inView || doNotPreload) && (
        <picture>
          {imgConfig.map(({ breakpoint, url, url2x }) => {
            const media = getMediaByBreakpoint(breakpoint);
            const srcSet = url2x ? `${url} 1x, ${url2x} 2x` : url;
            return media ? (
              <source key={breakpoint} srcSet={srcSet} media={media} />
            ) : null;
          })}
          {/* fallback для самых старых браузеров */}
          <img
            src={defaultConfig?.url || src}
            srcSet={
              defaultConfig?.url2x
                ? `${defaultConfig.url} 1x, ${defaultConfig.url2x} 2x`
                : undefined
            }
            alt={alt}
            width={width}
            height={height}
            style={{
              // width: "100%",
              // height: "100%",
              objectFit: "cover",
              opacity: isLoaded ? 1 : 0,
              transition: "opacity 0.4s",
              ...imgStyle,
            }}
            onLoad={() => setIsLoaded(true)}
            draggable={false}
          />
        </picture>
      )}
    </div>
  );
};
